import React, { Component, ErrorInfo, ReactNode } from 'react';
import { clearIndexedDb } from '../utils/indexedDbStorage';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleClearAndReload = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      await clearIndexedDb();
      if (window.caches) {
        try {
          const keys = await window.caches.keys();
          await Promise.all(keys.map(key => window.caches.delete(key)));
        } catch (err) {
          console.error('Error clearing Cache Storage:', err);
        }
      }
    } catch (e) {
      console.error('Error clearing local data:', e);
    } finally {
      window.location.reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div 
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            padding: '24px',
            boxSizing: 'border-box',
            backgroundColor: '#f8fafc',
            color: '#0f172a'
          }} 
          className="bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100"
        >
          <div 
            style={{
              maxWidth: '500px',
              width: '100%',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
              padding: '32px',
              boxSizing: 'border-box',
              textAlign: 'center',
              border: '1px solid #e2e8f0'
            }} 
            className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
          >
            
            {/* Warning Icon */}
            <div 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                marginBottom: '20px'
              }} 
              className="bg-red-100 dark:bg-red-900/30 text-red-500"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '32px' }}>warning</span>
            </div>

            <h1 style={{
              fontSize: '20px',
              fontWeight: '600',
              margin: '0 0 12px 0',
              lineHeight: '1.4'
            }}>
              K-LyDapp no pudo cargar correctamente.
            </h1>
            
            <p style={{
              fontSize: '14px',
              margin: '0 0 24px 0',
              lineHeight: '1.5',
              color: '#64748b'
            }} className="text-slate-500 dark:text-slate-400">
              Esto puede deberse a una actualización reciente o datos temporales del navegador.
            </p>

            {/* Error detail accordion */}
            {this.state.error && (
              <details 
                style={{
                  textAlign: 'left',
                  marginBottom: '24px',
                  fontSize: '12px',
                  border: '1px solid #f1f5f9',
                  borderRadius: '6px',
                  backgroundColor: '#f8fafc'
                }} 
                className="border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50"
              >
                <summary 
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    color: '#475569'
                  }} 
                  className="text-slate-600 dark:text-slate-400"
                >
                  Ver detalles del error
                </summary>
                <div 
                  style={{
                    padding: '12px',
                    borderTop: '1px solid #f1f5f9',
                    overflowX: 'auto',
                    maxHeight: '150px'
                  }} 
                  className="border-slate-100 dark:border-slate-700 font-mono text-xs text-slate-800 dark:text-slate-300"
                >
                  <strong style={{ color: '#ef4444' }}>{this.state.error.toString()}</strong>
                  <pre style={{ margin: '8px 0 0 0', opacity: 0.8, whiteSpace: 'pre-wrap' }}>
                    {this.state.errorInfo?.componentStack || this.state.error.stack}
                  </pre>
                </div>
              </details>
            )}

            {/* Action buttons */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <button
                onClick={this.handleRetry}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#136dec',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                className="hover:bg-blue-600 active:bg-blue-700"
              >
                Reintentar
              </button>

              <button
                onClick={this.handleReload}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 dark:border-slate-600"
              >
                Recargar página
              </button>

              <button
                onClick={this.handleClearAndReload}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'transparent',
                  color: '#ef4444',
                  border: '1px dashed #fca5a5',
                  borderRadius: '8px',
                  fontWeight: '500',
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s, border-color 0.2s'
                }}
                className="hover:bg-red-50 hover:border-red-400 dark:hover:bg-red-950/20 dark:border-red-900/50"
              >
                Limpiar datos locales y recargar
              </button>
            </div>

          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

